import { useEffect, useRef, useState } from "react";
import { AdminCharacterSettings } from "./AdminCharacterSettings";
import { SettingsRequestError, settingsApi } from "./api";
import { SkeletonGrid } from "./Skeleton";
import type { AdminCharacterBinding, AdminCharacterImage, AdminCharacterProfile, AdminCharacterSdPrompts, CharacterManagementItem, CharacterSettingsMode, UserCharacterSummary } from "./types";
import { VoiceSourcePicker } from "./VoiceSourcePicker";
import { ImagePromptPicker } from "./ImagePromptPicker";

function characterListError(error: unknown): string {
  if (error instanceof SettingsRequestError) {
    if (error.status === 401) return "登录状态已失效，重新授权后再试。";
    if (error.status === 403) return "你暂时不能查看角色目录。";
  }
  return "无法读取可用角色目录，请稍后重试。";
}

export function UserCharacterSettings({ isAdmin, actorUid, mode, onProfileSaved, onSdPromptsSaved, onImageSaved, onBindingChanged, onInteractions }: {
  isAdmin: boolean;
  actorUid: string;
  mode?: CharacterSettingsMode;
  onProfileSaved?: (profile: AdminCharacterProfile) => void;
  onSdPromptsSaved?: (prompts: AdminCharacterSdPrompts) => void;
  onImageSaved?: (image: AdminCharacterImage) => void;
  onBindingChanged?: () => void;
  onInteractions?: () => void;
}) {
  const adminMode = mode === "admin" || (mode === undefined && isAdmin);
  const [characters, setCharacters] = useState<UserCharacterSummary[]>([]);
  const [adminBindings, setAdminBindings] = useState<AdminCharacterBinding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [voiceTarget, setVoiceTarget] = useState<UserCharacterSummary | null>(null);
  const [imagePromptTarget, setImagePromptTarget] = useState<UserCharacterSummary | null>(null);
  const [sharedVoiceTarget, setSharedVoiceTarget] = useState<UserCharacterSummary | null>(null);
  const loadGeneration = useRef(0);
  const loadedOnce = useRef(false);
  const pendingCreated = useRef(new Map<string, CharacterManagementItem>());

  function createdSummary(character: CharacterManagementItem): UserCharacterSummary {
    const { owner_uid: _ownerUid, owner_name: _ownerName, ...summary } = character;
    return summary;
  }

  function reconcileCharacters(summaries: UserCharacterSummary[], bindings: AdminCharacterBinding[]): { summaries: UserCharacterSummary[]; bindings: AdminCharacterBinding[] } {
    const summaryIds = new Set(summaries.map((character) => character.id));
    const bindingIds = new Set(bindings.map((binding) => binding.id));
    for (const [id] of pendingCreated.current) {
      if (summaryIds.has(id) && (!adminMode || bindingIds.has(id))) pendingCreated.current.delete(id);
    }
    const pending = [...pendingCreated.current.values()];
    return {
      summaries: [...summaries, ...pending.filter((character) => !summaryIds.has(character.id)).map(createdSummary)],
      bindings: [...bindings, ...pending.filter((character) => !bindingIds.has(character.id)).map(({ id, name, owner_uid, owner_name, binding_version }) => ({ id, name, owner_uid, owner_name, binding_version }))],
    };
  }

  function rememberCreated(character: CharacterManagementItem): void {
    pendingCreated.current.set(character.id, character);
    setCharacters((current) => [...current.filter((item) => item.id !== character.id), createdSummary(character)]);
    if (adminMode) setAdminBindings((current) => [...current.filter((item) => item.id !== character.id), character]);
  }

  function loadCharacters(): void {
    const generation = ++loadGeneration.current;
    if (!loadedOnce.current) setLoading(true);
    setError("");
    const request = adminMode
      ? Promise.all([settingsApi.loadUserCharacters(), settingsApi.loadAdminCharacters()]).then(([summaries, bindings]) => { if (generation === loadGeneration.current) { const reconciled = reconcileCharacters(summaries, bindings); setCharacters(reconciled.summaries); setAdminBindings(reconciled.bindings); } })
      : settingsApi.loadUserCharacters().then((summaries) => { if (generation === loadGeneration.current) { const reconciled = reconcileCharacters(summaries, []); setCharacters(reconciled.summaries); setAdminBindings([]); } });
    void request.catch((cause) => { if (generation === loadGeneration.current) setError(characterListError(cause)); })
      .finally(() => { if (generation === loadGeneration.current) { loadedOnce.current = true; setLoading(false); } });
  }

  useEffect(() => {
    setCharacters([]);
    setAdminBindings([]);
    setVoiceTarget(null);
    setImagePromptTarget(null);
    setSharedVoiceTarget(null);
    pendingCreated.current.clear();
    loadedOnce.current = false;
    setLoading(true);
    loadCharacters();
    return () => { loadGeneration.current += 1; };
  }, [adminMode, actorUid]);

  return <>
    {loading ? <SkeletonGrid items={5} variant="row" label="正在读取角色管理" />
      : error && characters.length === 0 ? <div className="panel-error" role="alert"><p>{error}</p><button className="secondary outlined" type="button" onClick={loadCharacters}>重试</button></div>
        : <>{error ? <div className="panel-error" role="alert"><p>{error} 已保留当前角色列表。</p><button className="secondary outlined" type="button" onClick={loadCharacters}>重试刷新</button></div> : null}<AdminCharacterSettings items={characters.map((character): CharacterManagementItem => {
          const binding = adminBindings.find((candidate) => candidate.id === character.id);
          return { ...character, can_manage: adminMode || character.can_manage, owner_uid: binding?.owner_uid ?? null, owner_name: binding?.owner_name ?? null, binding_version: binding?.binding_version ?? character.binding_version };
        })} isAdmin={adminMode} actorUid={actorUid} mode={mode} onCharacterCreated={rememberCreated} onCatalogChanged={loadCharacters} onVoiceSource={setVoiceTarget} onSharedVoiceSource={setSharedVoiceTarget} onImagePrompts={setImagePromptTarget} onInteractions={onInteractions} onBindingChanged={onBindingChanged} onProfileSaved={onProfileSaved} onSdPromptsSaved={onSdPromptsSaved} onImageSaved={onImageSaved} /></>}
    {voiceTarget ? <VoiceSourcePicker character={voiceTarget} onClose={() => setVoiceTarget(null)} /> : null}
    {sharedVoiceTarget ? <VoiceSourcePicker character={sharedVoiceTarget} scope="shared" onClose={() => setSharedVoiceTarget(null)} /> : null}
    {imagePromptTarget ? <ImagePromptPicker character={imagePromptTarget} onClose={() => setImagePromptTarget(null)} /> : null}
  </>;
}
